import { cloneElement, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useHasPermission } from "@/hooks/use-has-permission";
import type { CollectionActions } from "@/lib/permissions";

type RequirePermissionProps<T extends keyof CollectionActions> = {
	collection: T;
	action: keyof CollectionActions[T];
	children: ReactElement;
	requireSubscription?: boolean;
};

/**
 * Declarative permission gate.
 *
 * - If the user's role forbids the action → renders `fallback` (default `null`).
 * - If the role allows it but the subscription is blocked → renders children
 *   (the action callback should call `showBlockedModal` at execution time).
 * - If both role and subscription allow it → renders children.
 *
 * For imperative checks (e.g. inside callbacks), use `useHasPermission` directly.
 */
export function RequirePermission<T extends keyof CollectionActions>({
	collection,
	action,
	children,
	requireSubscription = true,
}: RequirePermissionProps<T>) {
	const { blockedByRole, blockedBySubscription, showBlockedModal, isLoading } = useHasPermission({
		collection,
		action,
	});
	const { t } = useTranslation();

	if (!(isLoading || blockedByRole || (requireSubscription && blockedBySubscription))) {
		return children;
	}

	const childrenProps = children.props as HTMLButtonElement | HTMLAnchorElement;

	const props = {
		onClick: blockedBySubscription ? showBlockedModal : undefined,
		onKeyDown: blockedBySubscription ? showBlockedModal : undefined,
		onKeyUp: blockedBySubscription ? showBlockedModal : undefined,
		onKeyPress: blockedBySubscription ? showBlockedModal : undefined,
		type: childrenProps.type === "submit" ? "button" : childrenProps.type,
		href: undefined,
	};

	const disabledChildren = cloneElement(children, props);

	let content = "";

	if (blockedByRole) {
		content = t(($) => $.common.errors["PERMISSION.OWNER_ONLY"]);
	}

	if (blockedBySubscription) {
		content = t(($) => $.common.errors["SUBSCRIPTION.INACTIVE"]);
	}
	if (isLoading) {
		content = t(($) => $.common.status.loading);
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild className="opacity-50">
				{disabledChildren}
			</TooltipTrigger>

			<TooltipContent side="bottom">{content}</TooltipContent>
		</Tooltip>
	);
}
